import {
  type Player,
  PlayerStatus,
  type Role,
  type Room,
  type ServerMsg,
  ServerMsgKind,
} from '@infiltre/shared';
import { type AsyncState, AsyncStatus } from '@/shared/lib/async';
import { uiEventBus } from '@/shared/lib/events';
import type { GameSlice } from '@/shared/lib/zustand-types';

export type EliminationEntry = {
  round: number;
  playerId: string;
  name: string;
  role: Role;
  /** Players still alive AFTER this elimination, for the recap "x left in" line. */
  aliveAfter: number;
};

export type GameRoundSlice = {
  room: AsyncState<Room>;
  privateAssignment: { role: Player['role']; word: string } | null;
  /**
   * Bounded queue of recent emoji reactions (last 32). Driven by inbound
   * `Reaction` server messages; consumed by `ReactionBar` to render bursts
   * for everyone in the room. Keyed by `(playerId, at)` so dedup is trivial.
   */
  reactions: ReadonlyArray<{ playerId: string; emoji: string; at: number }>;
  /** Append-only log driven by `PlayerEliminated`; cleared by `resetRoom`. */
  eliminationHistory: readonly EliminationEntry[];

  applyServerMsg: (msg: ServerMsg, ctx: { selfPlayerId: string | null }) => void;
  resetRoom: () => void;
  setRoom: (room: Room) => void;
};

export const createGameRoundSlice: GameSlice<GameRoundSlice> = (set) => ({
  room: { status: AsyncStatus.Idle },
  privateAssignment: null,
  reactions: [],
  eliminationHistory: [],

  applyServerMsg: (msg, ctx) =>
    set((s) => {
      // RoomState is the sole writer to `s.room.data`. Per-event patches for
      // GameStarted/PhaseChanged/GameEnded were dropped — every one is
      // followed by a fresh RoomState in the same WS frame batch, and the
      // duplicate writes were the source of subtle ordering races.
      // Transient signals (Error/HostChanged/PresenceChanged) flow through
      // the UiEventBus instead of being mirrored into state.
      const onRoomState = (m: Extract<ServerMsg, { kind: typeof ServerMsgKind.RoomState }>) => {
        s.room = { status: AsyncStatus.Ready, data: m.room };
      };
      const onWordAssigned = (
        m: Extract<ServerMsg, { kind: typeof ServerMsgKind.WordAssigned }>,
      ) => {
        if (ctx.selfPlayerId !== m.playerId) return;
        s.privateAssignment = { role: m.role, word: m.word };
      };
      const onPresenceChanged = (
        m: Extract<ServerMsg, { kind: typeof ServerMsgKind.PresenceChanged }>,
      ) => {
        uiEventBus.emit('presence-changed', {
          playerId: m.playerId,
          presence: m.presence,
          at: Date.now(),
        });
        if (s.room.status !== AsyncStatus.Ready) return;
        const player = s.room.data.players.find((p) => p.id === m.playerId);
        if (!player) return;
        // Mutate in place so unrelated player rows keep shallow referential equality.
        player.presence = m.presence;
        if (m.lastSeenAt !== undefined) player.lastSeenAt = m.lastSeenAt;
      };
      const onReaction = (m: Extract<ServerMsg, { kind: typeof ServerMsgKind.Reaction }>) => {
        // Bounded queue — newest first, oldest dropped past 32 entries. The
        // ReactionBar mounts a per-entry timeout to clear its own DOM
        // bursts; the store keeps the last few so a remount during a phase
        // transition picks up the still-flying reactions.
        const next = [{ playerId: m.playerId, emoji: m.emoji, at: m.at }, ...s.reactions];
        s.reactions = next.slice(0, 32);
      };
      const onPlayerEliminated = (
        m: Extract<ServerMsg, { kind: typeof ServerMsgKind.PlayerEliminated }>,
      ) => {
        if (s.room.status !== AsyncStatus.Ready) return;
        const player = s.room.data.players.find((p) => p.id === m.playerId);
        if (!player) return;
        // PlayerEliminated arrives before the next RoomState — exclude this one to avoid a race.
        const aliveAfter = s.room.data.players.filter(
          (p) => p.status === PlayerStatus.Alive && p.id !== m.playerId,
        ).length;
        s.eliminationHistory = [
          ...s.eliminationHistory,
          {
            round: s.room.data.round,
            playerId: m.playerId,
            name: player.name,
            role: m.role,
            aliveAfter,
          },
        ];
      };

      switch (msg.kind) {
        case ServerMsgKind.RoomState:
          return onRoomState(msg);
        case ServerMsgKind.WordAssigned:
          return onWordAssigned(msg);
        case ServerMsgKind.HostChanged:
          uiEventBus.emit('host-changed', {
            newHostId: msg.newHostId,
            ...(msg.previousHostId !== undefined ? { previousHostId: msg.previousHostId } : {}),
            ...(msg.reason !== undefined ? { reason: msg.reason } : {}),
            at: Date.now(),
          });
          return;
        case ServerMsgKind.PresenceChanged:
          return onPresenceChanged(msg);
        case ServerMsgKind.Reaction:
          return onReaction(msg);
        case ServerMsgKind.Error:
          uiEventBus.emit('error', {
            code: msg.code,
            ...(msg.message !== undefined ? { message: msg.message } : {}),
            at: Date.now(),
          });
          return;
        case ServerMsgKind.PlayerEliminated:
          return onPlayerEliminated(msg);
        case ServerMsgKind.GameStarted:
        case ServerMsgKind.PhaseChanged:
        case ServerMsgKind.GameEnded:
        case ServerMsgKind.PlayerJoined:
        case ServerMsgKind.PlayerLeft:
        case ServerMsgKind.VoteCast:
        case ServerMsgKind.NextRoundRequested:
        case ServerMsgKind.Heartbeat:
          // Reflected in the next RoomState; Heartbeat is auto-handled by the driver.
          return;
      }
    }),

  resetRoom: () =>
    set((s) => {
      s.room = { status: AsyncStatus.Idle };
      s.privateAssignment = null;
      s.reactions = [];
      s.eliminationHistory = [];
    }),

  setRoom: (room) =>
    set((s) => {
      s.room = { status: AsyncStatus.Ready, data: room };
    }),
});
